import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Eye, Pencil, Check, X, Power, Trash2, Coins } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Avatar, EmptyState, PageHeader, PageLoader, Pagination, Panel, SearchInput, StatusBadge } from "@/components/common";
import { UserFormDialog, CreditDialog } from "./AdminDialogs";
import { UserDetailDialog } from "./UserDetailDialog";

const IconBtn = ({ icon: I, title, onClick, testid, tone = "" }) => (
  <button title={title} onClick={onClick} data-testid={testid} className={`rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 ${tone}`}><I className="h-4 w-4" /></button>
);

export default function UsersPage({ role, approvals = false }) {
  const label = role === "tutor" ? "Tutor" : "Student";
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(approvals ? "pending" : "all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ open: false, user: null });
  const [detail, setDetail] = useState(null);
  const [credit, setCredit] = useState(null);
  const [del, setDel] = useState(null);

  useEffect(() => { setStatus(approvals ? "pending" : "all"); setPage(1); }, [approvals, role]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users", { params: { role, q, page, limit: 10, status: status === "all" ? "" : status } });
    setData(data);
  }, [role, q, page, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (fn, okMsg) => {
    try { const r = await fn(); toast.success(r?.data?.message || okMsg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title={approvals ? `${label} Approvals` : `${label} Management`}
        subtitle={approvals ? `Review pending ${role} registrations.` : `Manage all ${role}s on FosterApp.`}
        actions={!approvals && <Button onClick={() => setForm({ open: true, user: null })} className="rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid={`add-${role}-button`}><Plus className="h-4 w-4" />Add {label}</Button>} />
      <Panel>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder={`Search ${role}s`} testid={`${role}-search-input`} />
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="h-10 rounded-full sm:w-48" data-testid={`${role}-status-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "pending", "approved", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!data ? <PageLoader /> : data.items.length === 0 ? <div className="p-6"><EmptyState title={`No ${role}s found`} text={approvals ? "Nothing waiting for review." : "Try adjusting search or filters."} /></div> : (
          <div className="overflow-x-auto">
            <Table data-testid={`${role}-table`}>
              <TableHeader><TableRow>
                <TableHead>{label}</TableHead>
                <TableHead>{role === "tutor" ? "Subject" : "Education"}</TableHead>
                {role === "tutor" ? <TableHead>Experience</TableHead> : <TableHead>Credits</TableHead>}
                <TableHead>Status</TableHead><TableHead>Joined</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map((u) => (
                  <TableRow key={u.id} data-testid={`${role}-row-${u.id}`} className={!u.is_active ? "opacity-60" : ""}>
                    <TableCell><div className="flex items-center gap-3"><Avatar src={u.profile_picture} name={u.name} size="h-9 w-9" />
                      <div><p className="font-semibold text-[#0B1A33]">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div></div></TableCell>
                    <TableCell>{role === "tutor" ? <span>{u.subject}<span className="block text-xs text-slate-500">{u.specialization}</span></span> : u.education_level || "—"}</TableCell>
                    <TableCell>{role === "tutor" ? `${u.experience} yrs` : <span className="font-semibold" data-testid={`student-credits-${u.id}`}>{u.wallet?.available ?? 0}</span>}</TableCell>
                    <TableCell><div className="flex flex-col items-start gap-1"><StatusBadge status={u.approval_status} testid={`${role}-status-${u.id}`} />{!u.is_active && <StatusBadge status="inactive" />}{!u.email_verified && <span className="text-[10px] text-amber-600">email unverified</span>}</div></TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(u.created_at)}</TableCell>
                    <TableCell><div className="flex justify-end">
                      {u.approval_status !== "approved" && <IconBtn icon={Check} title="Approve" tone="hover:text-[#2DA638]" testid={`approve-${role}-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/decision/approve`))} />}
                      {u.approval_status === "pending" && <IconBtn icon={X} title="Reject" tone="hover:text-red-600" testid={`reject-${role}-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/decision/reject`))} />}
                      <IconBtn icon={Eye} title="View profile" testid={`view-${role}-${u.id}`} onClick={() => setDetail(u.id)} />
                      <IconBtn icon={Pencil} title="Edit" testid={`edit-${role}-${u.id}`} onClick={() => setForm({ open: true, user: u })} />
                      {role === "student" && <IconBtn icon={Coins} title="Credits" tone="hover:text-[#2DA638]" testid={`credits-${role}-${u.id}`} onClick={() => setCredit(u)} />}
                      <IconBtn icon={Power} title={u.is_active ? "Deactivate" : "Activate"} testid={`toggle-active-${role}-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/status`, { is_active: !u.is_active }))} />
                      <IconBtn icon={Trash2} title="Delete" tone="hover:text-red-600" testid={`delete-${role}-${u.id}`} onClick={() => setDel(u)} />
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
          </div>
        )}
      </Panel>
      <UserFormDialog role={role} open={form.open} user={form.user} onClose={() => setForm({ open: false, user: null })} onSaved={load} />
      <UserDetailDialog userId={detail} onClose={() => setDetail(null)} />
      <CreditDialog student={credit} onClose={() => setCredit(null)} onSaved={load} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the account. Users with session history can only be deactivated.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button" onClick={() => act(() => api.delete(`/admin/users/${del.id}`))}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
